import Delete from '@mui/icons-material/Delete';
import Card from '@mui/material/Card';
import { useDraggable, useDroppable } from '@dnd-kit/react';
import { GridDragIcon } from '@mui/x-data-grid-premium';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { dragIconSx, groupingCardSx } from '../styles';
import { ComparativeColumn, GroupedColumn } from '../../requests/index';
import { getPeriodLabel } from './DateUtils';
import { innerComparisonFormSx, mlAuto } from './styles';

export function GroupingCard({
  col,
  showDragIcon,
  setGroupedColumns,
}: {
  col: GroupedColumn;
  showDragIcon: boolean;
  setGroupedColumns: React.Dispatch<React.SetStateAction<GroupedColumn[]>>;
}) {
  return (
    <Card sx={groupingCardSx} key={col.field} data-testid={`grouping-card-${col.field}`}>
      <Grid wrap='nowrap' container alignItems='center' gap={1}>
        {showDragIcon && <GridDragIcon sx={dragIconSx} />}
        <Typography>{col.field}</Typography>
        <IconButton
          color='error'
          sx={mlAuto}
          onClick={() => setGroupedColumns((prev) => prev.filter((c) => c.field !== col.field))}
        >
          <Delete fontSize='small' />
        </IconButton>
      </Grid>
    </Card>
  );
}

export function ComparativeCard({
  col,
  showDragIcon,
  comparisonOptions,
  t,
  setComparativeColumns,
}: {
  col: ComparativeColumn;
  showDragIcon: boolean;
  comparisonOptions: string[];
  t: (key: string) => string;
  setComparativeColumns: React.Dispatch<React.SetStateAction<ComparativeColumn[]>>;
}) {
  return (
    <Card
      sx={groupingCardSx}
      key={`${col.field}-${col.dateRange ?? ''}`}
      data-testid={`comparative-card-${col.field}-${col.dateRange ?? ''}`}
    >
      <Grid wrap='nowrap' container alignItems='center'>
        {showDragIcon && <GridDragIcon sx={dragIconSx} />}
        <Typography>{col.field}</Typography>
        <IconButton
          color='error'
          sx={mlAuto}
          onClick={() =>
            setComparativeColumns((prev) =>
              prev.filter((c) => !(c.field === col.field && c.dateRange === col.dateRange))
            )
          }
        >
          <Delete fontSize='small' />
        </IconButton>
      </Grid>
      <Grid>
        <FormControl fullWidth variant='standard' sx={innerComparisonFormSx}>
          <InputLabel>{t('admin.customReports.editReports.comparisonDateRange')}</InputLabel>
          <Select
            variant='standard'
            fullWidth
            value={col.dateRange}
            onChange={(e) =>
              setComparativeColumns((prev) =>
                prev.map((c: ComparativeColumn, i) =>
                  c.field === col.field && col.dateRange === c.dateRange ? { ...c, dateRange: e.target.value } : c
                )
              )
            }
          >
            {comparisonOptions.map((key) => (
              <MenuItem key={key} value={key}>
                {getPeriodLabel(key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Card>
  );
}

export function DraggableCard({
  col,
  type,
  options,
  t,
  setState,
}: {
  col: GroupedColumn | ComparativeColumn;
  type: 'group' | 'comparative';
  // eslint-disable-next-line react/require-default-props
  options?: string[];
  // eslint-disable-next-line react/require-default-props
  t?: (key: string) => string;
  setState:
    | React.Dispatch<React.SetStateAction<GroupedColumn[]>>
    | React.Dispatch<React.SetStateAction<ComparativeColumn[]>>;
}) {
  const { ref } = useDraggable({
    id: type === 'group' ? `${col.field}` : `${col.field}_${(col as ComparativeColumn).dateRange}`,
  });

  return (
    <div ref={ref}>
      {type === 'group' && setState && (
        <GroupingCard
          col={col as GroupedColumn}
          showDragIcon
          setGroupedColumns={setState as React.Dispatch<React.SetStateAction<GroupedColumn[]>>}
        />
      )}

      {type === 'comparative' && setState && (
        <ComparativeCard
          col={col as ComparativeColumn}
          showDragIcon
          comparisonOptions={options ?? []}
          t={t ?? ((key: string) => key)}
          setComparativeColumns={setState as React.Dispatch<React.SetStateAction<ComparativeColumn[]>>}
        />
      )}
    </div>
  );
}

export function DroppableZone({ id, children }: { id: string; children: React.ReactNode }) {
  const { ref } = useDroppable({
    id,
  });
  return <div ref={ref}>{children}</div>;
}
